<script>
    import dressing from "@intellectability/platform-common";
    import {AutoUpdateInput, MegaPopup, Modal, toast} from "@intellectability/platform-common/sv";
    import {ModalBody, ModalFooter, Row, Col, Button} from "@sveltestrap/sveltestrap";
    import {createForm} from "svelte-forms-lib";
    import * as yup from "yup";
    import EntityChangeHistory from "./EntityChangeHistory.svelte";

    /** @import { Snippet } from 'svelte' */;

    /**
     * @typedef {Object} props
     * @property {function} [onopen]
     * @property {function} [onclose]
     * @property {string} [criticalIncidentId]
     * @property {string} [criticalIncidentConsId]
     * @property {boolean} [modalOpen]
     * @property {Array<{id: string, status: string}>} criticalIncidentStatuses
     */
    /** @type {props} */
    let {
        onopen = null,
        onclose = null,
        criticalIncidentId = '0', // criticalIncidentId for editing an existing critical incident
        criticalIncidentConsId = undefined, // cons_id for creating a new critical incident
        modalOpen = $bindable(false),
        criticalIncidentStatuses,
    } = $props();

    let loading = $state(false);

    /** @type {CriticalIncidentListEntry} */ // @ts-ignore
    let criticalIncident = $state({});
    let criticalIncidentExists = $derived(criticalIncidentId && criticalIncidentId !== '0');

    let initialValues = $derived({
        id: criticalIncidentId + "",
        cons_id: criticalIncidentConsId ?? "",
        status_name: criticalIncident.status_name ?? "",
        incident_date: criticalIncident.incident_date ?? "",
        incident_date_fmt: criticalIncident.incident_date_fmt ?? "",
        incident_time: criticalIncident.incident_time ?? "",
        description: criticalIncident.description ?? "",
        reported_by: criticalIncident.reported_by ?? "",
        provider: criticalIncident.provider ?? "",
        agencies_contacted: criticalIncident.agencies_contacted ?? "",
        witness_info: criticalIncident.witness_info ?? "",
        details: criticalIncident.details ?? "",
        location: criticalIncident.location ?? "",
        follow_up: criticalIncident.follow_up ?? "",
    });

    let field_margin_classes = "mb-4 mb-lg-4";
    const show_floating_labels = true;

    let logDisplayed = $state(false);

    /**
     * Transforms an array of objects into an array of {value, label} objects.
     * @param {Object[]} array - The input array of objects.
     * @param {string} valueKey - The key name to use for the value property.
     * @param {string} labelKey - The key name to use for the label property.
     * @returns {{value: string, label: string}[]} An array of objects with {value, label}.
     */
    function transformArrayForSelect(array, valueKey, labelKey) {
        return array.map(item => ({
            value: String(item[valueKey]),
            label: String(item[labelKey])
        }));
    }

    // for dropdowns
    let criticalIncidentStatusesOptions = $derived((criticalIncidentStatuses ?? []).map(function (status) {
        return {label: status.status, value: status.status};
    }));
    let providerOptions = $state.raw([]);

    const form_props = {
        validationSchema: yup.object().shape({
            incident_date: yup.string().required(),
            incident_time: yup.string().required(),
            status_name: yup.string().required(),
            description: yup.string().required(),
            reported_by: yup.string(),
            provider: yup.string(),
            agencies_contacted: yup.string(),
            witness_info: yup.string(),
            location: yup.string(),
            details: yup.string().required(),
            follow_up: yup.string(),
        })
    };

    let formDisabledBySecurity = false;

    const {form, errors, handleSubmit, updateInitialValues} = 
        createForm({
            initialValues,
            validationSchema: form_props.validationSchema,
            onSubmit: () => on_submit(),
        });
    
    $effect(function () {
        updateInitialValues(initialValues);
    });

    $effect(function load() {
        if(Number($form.cons_id) > 0){
            const needed_initial_data = [
                dressing.get(`/people/providers/${criticalIncidentConsId}`).then(({data: response}) => {
                    const {success, data} = response;
                    if (success === true) {
                        const {providers} = data;
                        if (Array.isArray(providers) && providers.length > 0) {
                            providerOptions = transformArrayForSelect(providers, "id", "name");
                        }
                        return true;
                    }
                    return false;
                }),
            ];

            Promise.allSettled(needed_initial_data).then(function (resolved) {
                // @ts-ignore
                const success = resolved.every(r => r.value === true);
                if (success) {
                    return loading = false;
                }
            });
        }
    });

    $effect(function () {
        if (modalOpen && criticalIncidentId && criticalIncidentId !== '0') {
            loading = true;
            dressing.get(`/cm/critical_incidents/${criticalIncidentId}`)
                .then(({data: response}) => {
                    const {data} = response;
                    if(data.critical_incident) {
                        criticalIncident = data.critical_incident;
                    } else {
                        //@ts-ignore
                        criticalIncident = {};
                    }
                    //typeof onopen === "function" && onopen(value);
                    loading = false;
                })
                .catch(error => {
                        toast.error("Failed to load Critical Incident details.");
                        loading = false;
                    }
                );
        }else if(modalOpen && criticalIncidentConsId) {
            // @ts-ignore
            criticalIncident = {};
            loading = false;
        }
    });

    function toggle_modal() {
        modalOpen = !modalOpen;
        if (modalOpen && typeof onopen === "function") {
            onopen();
        }
    }

    function on_submit() {
        let url = `/cm/critical_incidents/create`;
        if( $form.id && $form.id !== '0') url = `/cm/critical_incidents/update`;
        dressing.post(url, {
            ...$form
        }).then(
            ({data:response}) => {
                const {success, data} = response;
                if (success === true) {
                    toggle_modal();
                    toast.success(`Critical Incident successfully created/updated.`);
                }
            }
        ).catch(
            (error) => {
                toast.error(`Error creating/updating Critical Incident.`);
            }
        );
    }
    
    function displayLogs() {
        logDisplayed = !logDisplayed;
    }

    function closeCriticalIncidentModal() {
        criticalIncidentId = '0';
        criticalIncidentConsId = undefined;
        // @ts-ignore
        criticalIncident = {};
        if(typeof onclose === "function"){
            onclose();
        }
    }
</script>

<Modal bind:showModal={modalOpen} title="Critical Incident Details" size="xl" onclose={closeCriticalIncidentModal}>
    <ModalBody>
        <h3>{criticalIncident.cons_name}</h3>
        <wa-callout class="mb-4 bg-primary-50 text-secondary-900 border-primary" style="white-space: pre-line" appearance="outlined filled">
            <wa-icon slot="icon" name="circle-info" variant="regular"></wa-icon>
            <div class="no-break-word">
                Use this form to document critical incidents relating to the person. This could be something that required a hospital visit or emergency personnel, or other incidents that required intervention of some type; anything that resulted in a call to 911, etc.
            </div>
        </wa-callout>
        <form onsubmit={handleSubmit} novalidate>
            {#key criticalIncident.id}
                <fieldset disabled={loading} id="loading-fieldset">
                    <Row class="">
                        <Col xs="12" lg="6" class={field_margin_classes}>
                            <AutoUpdateInput
                                    floatingLabel={show_floating_labels}
                                    fieldName="incident_date"
                                    loading={loading}
                                    disabled={formDisabledBySecurity}
                                    bind:defaultValue={$form.incident_date}
                                    invalid={$errors.incident_date.length > 0}
                                    invalidMessage="Please provide a valid date"
                                    entityID={criticalIncident.id}
                                    type="date"
                                    required={true}
                                    label="Incident Date"/>
                        </Col>
                        <Col xs="6" lg="6" class={field_margin_classes}>
                            <AutoUpdateInput
                                    floatingLabel={show_floating_labels}
                                    fieldName="incident_time"
                                    loading={loading}
                                    bind:defaultValue={$form.incident_time}
                                    entityID={criticalIncident.id}
                                    type="time"
                                    invalid={$errors.incident_time.length > 0}
                                    invalidMessage="Please select the incident time"
                                    required={true}
                                    label={"Incident Time"}/>
                        </Col>
                        <Col xs="12" lg="6" class={field_margin_classes}>
                            <AutoUpdateInput
                                    floatingLabel={show_floating_labels}
                                    fieldName="status_name"
                                    loading={loading}
                                    bind:defaultValue={$form.status_name}
                                    entityID={criticalIncident.id}
                                    type="select"
                                    invalid={$errors.status_name.length > 0}
                                    invalidMessage="Please select the incident status"
                                    optionsFrom={criticalIncidentStatusesOptions}
                                    required={true}
                                    label="Status"/>
                        </Col>
                        <Col xs="12" lg="6" class={field_margin_classes}>
                            <AutoUpdateInput
                                    floatingLabel={show_floating_labels}
                                    fieldName="description"
                                    loading={loading}
                                    bind:defaultValue={$form.description}
                                    entityID={criticalIncident.id}
                                    type="text"
                                    invalid={$errors.description.length > 0}
                                    invalidMessage="Please insert a brief description of the incident"
                                    required={true}
                                    label={"Title (Brief description)"}/>
                        </Col>
                        <Col xs="12" lg="6" class={field_margin_classes}>
                            <AutoUpdateInput
                                    floatingLabel={show_floating_labels}
                                    fieldName="provider"
                                    loading={loading}
                                    bind:defaultValue={$form.provider}
                                    entityID={criticalIncident.id}
                                    type="select"
                                    invalid={$errors.provider.length > 0}
                                    invalidMessage="Please select the service"
                                    optionsFrom={providerOptions}
                                    label={"Provider"}/>
                        </Col>
                    </Row>
                    <Row class="">
                        <Col xs="12" lg="6" class={field_margin_classes}>
                            <AutoUpdateInput
                                    floatingLabel={show_floating_labels}
                                    fieldName="reported_by"
                                    loading={loading}
                                    bind:defaultValue={$form.reported_by}
                                    entityID={criticalIncident.id}
                                    type="textarea"
                                    invalid={$errors.reported_by.length > 0}
                                    invalidMessage="Please insert the name of the person who reported the incident"
                                    label={"Reported By"}/>
                        </Col>
                        <Col xs="12" lg="6" class={field_margin_classes}>
                            <AutoUpdateInput
                                    floatingLabel={show_floating_labels}
                                    fieldName="agencies_contacted"
                                    loading={loading}
                                    bind:defaultValue={$form.agencies_contacted}
                                    entityID={criticalIncident.id}
                                    type="textarea"
                                    invalid={$errors.agencies_contacted.length > 0}
                                    invalidMessage="Please insert any contacted agencies"
                                    label={"Agencies Contacted"}/>
                        </Col>
                        <Col xs="12" lg="6" class={field_margin_classes}>
                            <AutoUpdateInput
                                    floatingLabel={show_floating_labels}
                                    fieldName="witness_info"
                                    loading={loading}
                                    bind:defaultValue={$form.witness_info}
                                    entityID={criticalIncident.id}
                                    type="textarea"
                                    invalid={$errors.witness_info.length > 0}
                                    invalidMessage="Please insert any witness information"
                                    label={"Witness Info"}/>
                        </Col>
                        <Col xs="12" lg="6" class={field_margin_classes}>
                            <AutoUpdateInput
                                    floatingLabel={show_floating_labels}
                                    fieldName="location"
                                    loading={loading}
                                    bind:defaultValue={$form.location}
                                    entityID={criticalIncident.id}
                                    type="textarea"
                                    invalid={$errors.location.length > 0}
                                    invalidMessage="Please insert the location of the incident"
                                    label={"Location"}/>
                        </Col>
                        <Col xs="12" lg="6" class={field_margin_classes}>
                            <AutoUpdateInput
                                    floatingLabel={show_floating_labels}
                                    fieldName="details"
                                    loading={loading}
                                    bind:defaultValue={$form.details}
                                    entityID={criticalIncident.id}
                                    type="textarea"
                                    invalid={$errors.details.length > 0}
                                    invalidMessage="Please insert the details of the incident"
                                    required={true}
                                    label={"Details"}/>
                        </Col>
                        <Col xs="12" lg="6" class={field_margin_classes}>
                            <AutoUpdateInput
                                    floatingLabel={show_floating_labels}
                                    fieldName="follow_up"
                                    loading={loading}
                                    bind:defaultValue={$form.follow_up}
                                    entityID={criticalIncident.id}
                                    type="textarea"
                                    invalid={$errors.follow_up.length > 0}
                                    invalidMessage="Please insert any follow up information"
                                    label={"Follow Up"}/>
                        </Col>
                    </Row>
                    <Button color="primary" class="rounded-pill">Submit</Button>
                </fieldset>
            {/key}
        </form>
    </ModalBody>
    <ModalFooter class="justify-content-start">
        <div class="ms-2">
            {#if criticalIncidentExists}
                <button id="change_log_button" class="btn btn-primary btn-sm rounded-pill" onclick={displayLogs}>
                    {"Change Log"}
                </button>
            {/if}
            <MegaPopup
                bind:active={logDisplayed}
                for="change_log_button"
                placement="top-start">
                <div class="pt-2 px-3 rounded-2 bg-white">
                    <EntityChangeHistory entity={"critical_incident"} id={criticalIncident.id}></EntityChangeHistory>
                </div>
            </MegaPopup>
        </div>
    </ModalFooter>
</Modal>

<style>
    .no-break-word {
        word-break: keep-all !important;
        overflow-wrap: normal !important;
        hyphens: none !important;
    }
</style>